import { CohortEnum } from '@eigtech/auth0-types'
import { GetMeetingVideoResponse, RelativeTranscriptEvent } from '@eigtech/meetings-types'
import { useIsMutating } from '@eigtech/ui-shared-api'
import { QueryErrorBoundary } from '@eigtech/ui-shared-api-web'
import {
  hoursToSeconds,
  minutesToSeconds,
  secondsToHours,
  secondsToMinutes,
} from '@eigtech/ui-shared-dates'
import {
  AspectRatio,
  Box,
  Button,
  ButtonGroup,
  Center,
  CircularProgress,
  ComposedAlert,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Skeleton,
  SkeletonText,
  Stack,
  chakra,
} from '@eigtech/ui-shared-dave'
import { FileUpload } from '@eigtech/ui-shared-files'
import { useQuerySearch } from '@eigtech/ui-shared-search'
import { Dispatch, RefObject, SetStateAction, useEffect, useMemo, useRef, useState } from 'react'
import { MdAddAPhoto, MdSearch } from 'react-icons/md'
import { meetingsMutationKeys } from '../../api/constants'
import { useGetMeetingVideo } from '../../api/getMeetingVideo'
import { useGetMeetingTranscript } from '../../api/getTranscript'
import { useUploadScreenshot } from '../../api/uploadScreenshot'
import { getFileFromCanvas } from '../../utils/getFileFromCanvas'
import { MeetingInfo } from './types'

export type MeetingVideoProps = MeetingInfo & {
  cohort: CohortEnum
  getMetaForScreenshotUpload?: (image: File) => Promise<Record<string, string> | undefined>
}

export function MeetingVideo({ cohort, entityId, meetingId, startTime }: MeetingVideoProps) {
  const { data: video, isPending: isPendingVideo } = useGetMeetingVideo({
    entityId,
    meetingId,
    startTime,
  })

  const videoRef = useRef<HTMLVideoElement>(null)

  const [currentTime, setCurrentTime] = useState(0)

  return (
    <Stack direction={{ base: 'column', xl: 'row' }} spacing="4">
      <Box w="full">
        <Center>
          <Box maxW="60rem" w="full">
            {isPendingVideo ? (
              <AspectRatio ratio={16 / 9} w="full">
                <Skeleton h="full" w="full" />
              </AspectRatio>
            ) : video?.videoUrl ? (
              <Video
                cohort={cohort}
                entityId={entityId}
                meetingId={meetingId}
                setCurrentTime={setCurrentTime}
                video={video}
                videoRef={videoRef}
              />
            ) : (
              <ComposedAlert
                alert={{
                  title: 'No video available for this inspection.',
                  description:
                    'If the inspection ended recently, it may take a few minutes for the video to be ready for display.',
                }}
              />
            )}
          </Box>
        </Center>
      </Box>

      <QueryErrorBoundary>
        <TranscriptContainer
          currentTime={currentTime}
          entityId={entityId}
          meetingId={meetingId}
          startTime={startTime}
          videoRef={videoRef}
        />
      </QueryErrorBoundary>
    </Stack>
  )
}

type VideoProps = {
  setCurrentTime: Dispatch<SetStateAction<number>>
  video: GetMeetingVideoResponse
  videoRef: RefObject<HTMLVideoElement | null>
} & Pick<MeetingVideoProps, 'cohort' | 'entityId' | 'getMetaForScreenshotUpload' | 'meetingId'>

function Video({
  cohort,
  entityId,
  getMetaForScreenshotUpload,
  meetingId,
  setCurrentTime,
  video,
  videoRef,
}: VideoProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [videoMetaDataLoaded, setVideoMetaDataLoaded] = useState(false)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    if (video.readyState >= 1) {
      setVideoMetaDataLoaded(true)
      return
    }

    function onLoad() {
      setVideoMetaDataLoaded(true)
    }

    video.addEventListener('loadedmetadata', onLoad)

    return () => {
      video.removeEventListener('loadedmetadata', onLoad)
    }
  }, [videoRef])

  function handleProgress() {
    const video = videoRef.current
    if (!video) return

    setCurrentTime(video.currentTime)
  }

  const { mutateAsync: uploadScreenshot } = useUploadScreenshot({
    cohort,
    entityId: entityId,
    meetingId: meetingId,
  })

  async function takeScreenshot() {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!(video && canvas)) return

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    const context = canvas.getContext('2d')
    if (!context) return

    context.clearRect(0, 0, canvas.width, canvas.height)

    context.drawImage(video, 0, 0, canvas.width, canvas.height)

    const file = await getFileFromCanvas({ canvasRef, meetingId })

    const payload: FileUpload = { file, 'Content-Type': 'image/png' }

    if (getMetaForScreenshotUpload) {
      payload.meta = await getMetaForScreenshotUpload(file)
    }

    payload.meta = {
      ...payload.meta,
      photo: JSON.stringify({ videoTime: Math.round(video.currentTime * 1000) }),
    }

    uploadScreenshot(payload)
  }

  const isUploading = useIsMutating({
    mutationKey: meetingsMutationKeys.uploadScreenshot({
      entityId,
      meetingId,
    }),
  })

  return (
    <>
      <chakra.canvas ref={canvasRef} display="none" />
      <Stack>
        <chakra.video
          ref={videoRef}
          aspectRatio={16 / 9}
          controls
          crossOrigin="anonymous"
          src={video?.videoUrl}
          w="full"
          onTimeUpdate={handleProgress}
        />
        <ButtonGroup>
          <Button
            isDisabled={!videoMetaDataLoaded}
            leftIcon={isUploading ? <CircularProgress isIndeterminate size={5} /> : <MdAddAPhoto />}
            variant="outline"
            onClick={takeScreenshot}
          >
            Screenshot
          </Button>
        </ButtonGroup>
      </Stack>
    </>
  )
}

function TranscriptContainer({
  currentTime,
  entityId,
  meetingId,
  videoRef,
}: MeetingInfo & { currentTime: number; videoRef: RefObject<HTMLVideoElement | null> }) {
  const { data: meetingTranscript, isPending: isPendingTranscript } = useGetMeetingTranscript({
    entityId,
    meetingId,
  })

  const transcriptWithId = useMemo(
    () =>
      (meetingTranscript?.transcript ?? []).map((transcript) => ({
        ...transcript,
        id: `${transcript.speakerName}${transcript.text}${transcript.seconds}`,
      })),
    [meetingTranscript?.transcript]
  )

  const { query, setQuery, searchResults } = useQuerySearch(transcriptWithId, {
    fields: ['text', 'speakerName'],
    idField: 'id',
  })

  const transcript = useMemo(
    () =>
      [...((query ? searchResults : transcriptWithId) ?? [])].sort((a, b) => a.seconds - b.seconds),
    [query, searchResults, transcriptWithId]
  )

  const transcriptRef = useRef<HTMLDivElement>(null)
  const [activeTranscript, setActiveTranscript] = useState<RelativeTranscriptEvent | null>(null)
  useEffect(() => {
    const currentSecond = Math.ceil(currentTime)
    const transcripts = [...(transcript ?? [])].reverse()
    const activeTranscript = transcripts.find(({ seconds }) => Math.floor(seconds) <= currentSecond)
    setActiveTranscript(activeTranscript ?? null)
  }, [currentTime, transcript])

  useEffect(() => {
    if (!activeTranscript) return

    const transcriptEl = document.getElementById(getTranscriptId(activeTranscript.seconds))
    if (!transcriptEl) return

    const parent = transcriptEl.parentNode as HTMLDivElement
    parent.scrollTop = transcriptEl.offsetTop
  }, [activeTranscript])

  return (
    <Stack spacing="4" w={{ base: 'full', xl: '50%' }}>
      <FormControl>
        <FormLabel fontSize="sm" htmlFor="searchTranscript">
          Search Transcript
        </FormLabel>
        <InputGroup>
          <Input
            name="searchTranscript"
            placeholder="Search transcript..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <InputRightElement>
            <MdSearch />
          </InputRightElement>
        </InputGroup>
      </FormControl>

      <Box flexGrow={1} position="relative">
        <Stack
          ref={transcriptRef}
          h={{ base: '40vh', xl: 'full' }}
          overflow="auto"
          position={{ base: 'relative', xl: 'absolute' }}
          w="full"
        >
          {isPendingTranscript ? (
            <SkeletonText noOfLines={10} />
          ) : transcript?.length ? (
            transcript.map((transcript) => (
              <Transcript
                key={transcript.id}
                {...transcript}
                activeTranscript={activeTranscript}
                videoRef={videoRef}
              />
            ))
          ) : query ? (
            <ComposedAlert alert="No match found." />
          ) : (
            <ComposedAlert alert="No transcript available for this inspection." />
          )}
        </Stack>
      </Box>
    </Stack>
  )
}

function Transcript({
  activeTranscript,
  seconds,
  speakerName,
  text,
  videoRef,
}: RelativeTranscriptEvent & {
  activeTranscript: RelativeTranscriptEvent | null
  videoRef: RefObject<HTMLVideoElement | null>
}) {
  const hours = secondsToHours(seconds)
  const hoursInSeconds = hoursToSeconds(hours)
  const minutes = secondsToMinutes(seconds - hoursInSeconds)
  const minutesInSeconds = minutesToSeconds(minutes)
  const remainingSeconds = Math.floor(seconds - hoursInSeconds - minutesInSeconds)

  const options: Intl.NumberFormatOptions = {
    minimumIntegerDigits: 2,
    useGrouping: false,
  }
  const timestamp = `${hours.toLocaleString('en-US', options)}:${minutes.toLocaleString(
    'en-US',
    options
  )}:${remainingSeconds.toLocaleString('en-US', options)}`

  const isActiveTranscript = seconds === activeTranscript?.seconds

  return (
    <Box id={getTranscriptId(seconds)} pr="2">
      <chakra.figure
        backgroundColor={isActiveTranscript ? 'blue.50' : undefined}
        borderLeft="4px"
        borderLeftColor={isActiveTranscript ? 'blue.300' : 'gray.200'}
        cursor="pointer"
        pl="2"
        py="0.5"
        onClick={() => {
          if (!videoRef.current) return
          videoRef.current.currentTime = seconds
        }}
      >
        <chakra.blockquote>{text}</chakra.blockquote>
        <chakra.figcaption fontSize="xs" fontStyle="italic">
          <chakra.span>{speakerName}</chakra.span> @ {timestamp}
        </chakra.figcaption>
      </chakra.figure>
    </Box>
  )
}

const getTranscriptId = (seconds: number) => `transcript-${seconds}`
